import { FilterConfig } from "./filter-url-sync.service";
import {
  defaultTornOption,
  defaultUnknownOption,
  filterTornOptions,
  filterUnknownOptions
} from "./device-container-state-controller.service";
import type { IGraphFrontConfig } from "../components/pages/analytics/graph-config";

/**
 * Per-page URL ⇄ filter-backend maps. Each page registers only its own subset.
 * Field/storage-key names are verified against the current page components.
 *
 * The URL is the source of truth: on load every param declared here is applied.
 * A param the URL carries empty is off; one it does not mention is restored from
 * its cache instead (see FilterUrlSyncService), which is what lets a filter
 * survive a tab hop through a page that does not declare it. An entry with a
 * `storageKey` is cached in
 * localStorage by the sync service (the single writer of both URL and storage);
 * one without lives on the page component (or in a signal store), which assigns it
 * from the values `hydrateFromUrl` returns and passes it back on every mirror.
 *
 * **`paramKey` is globally unique across every config in this file.** Two configs
 * may share a key only when it is literally the same filter (`pkg`, `trktyp`,
 * `torn`, `unk` on kpi/map/list/analytics) — never for two different ones, or a
 * tab link would carry a value that decodes into a nonsense filter on the target
 * page. `carriedParams` needs no exception list because of this.
 *
 * A radio filter is never "off" — it always has one option selected — so it
 * declares `defaultValue` (+ `allowedValues`) here instead: this file is the ONLY
 * place its base value is written down, and the URL always shows the option that
 * is actually applied (`torn=noTorn`, never `torn=`). Pages must not re-declare it.
 *
 * Naming notes (resolved during discovery):
 *  - `filterDevices` (component field) ⇄ `filterDeviceIds` (storage key): same filter.
 *  - `filterConfidenceRadius` (component field) ⇄ `filterRadius` (storage key): same filter.
 *  - `filterIncidents` (plural, map/list) ≠ `filterIncident` (singular, incident
 *    page, query object — NOT URL-synced).
 *  - status-days range (map/list, `days`) vs date range (analytics, `range`):
 *    different filter, same range encoding, different key — never collide.
 *  - packaging category: `pkgcat` (kpi/map/list) is a MULTISELECT of the checked
 *    categories, backed by `filterContainerCategory`; analytics' `cat` is a radio
 *    holding one of all/specific/polyvalent and lives on the component. Same
 *    concept, different shape and backend, so they are different filters and must
 *    keep different keys (see the uniqueness rule above).
 */

/**
 * KPI page: packaging types, tracker types, torn flag, unknown flag.
 */
export const KPI_FILTER_CONFIG = [
  { paramKey: "pkg", storageKey: "filterContainers", type: "array" },
  { paramKey: "pkgcat", storageKey: "filterContainerCategory", type: "array" },
  { paramKey: "trktyp", storageKey: "filterDevicesTypes", type: "array" },
  {
    paramKey: "torn",
    storageKey: "filterTorn",
    type: "string",
    defaultValue: defaultTornOption,
    allowedValues: Object.keys(filterTornOptions)
  },
  {
    paramKey: "unk",
    storageKey: "filterUnknown",
    type: "string",
    defaultValue: defaultUnknownOption,
    allowedValues: Object.keys(filterUnknownOptions)
  }
] as const satisfies FilterConfig;

/**
 * MAP and LIST share the same filter set (they filter the same tracker fleet).
 *  - `status` (colorStatus) has no `storageKey`: the shown status markers live in
 *    the AppStore signal store, which persists them itself — encoded/decoded here
 *    but never written to localStorage by this service.
 *  - `lastsite` is the last-SEEN-site filter (`filterLastSites`, plural, a
 *    multiselect), and is deliberately not analytics' `sites`. The single site whose
 *    view is OPEN is not here and is not a filter: it is a path segment,
 *    `/map/site/:site_code` (see RecordRoute), because opening one swaps the data
 *    source, pins the camera and opens a sidenav.
 */
export const MAP_LIST_FILTER_CONFIG = [
  { paramKey: "pkg", storageKey: "filterContainers", type: "array" },
  { paramKey: "pkgcat", storageKey: "filterContainerCategory", type: "array" },
  { paramKey: "trk", storageKey: "filterDeviceIds", type: "array" },
  { paramKey: "lastsite", storageKey: "filterLastSites", type: "array" },
  { paramKey: "inc", storageKey: "filterIncidents", type: "array" },
  { paramKey: "days", storageKey: "filterStatusDays", type: "range" },
  { paramKey: "rad", storageKey: "filterRadius", type: "array" },
  { paramKey: "trktyp", storageKey: "filterDevicesTypes", type: "array" },
  {
    paramKey: "torn",
    storageKey: "filterTorn",
    type: "string",
    defaultValue: defaultTornOption,
    allowedValues: Object.keys(filterTornOptions)
  },
  {
    paramKey: "unk",
    storageKey: "filterUnknown",
    type: "string",
    defaultValue: defaultUnknownOption,
    allowedValues: Object.keys(filterUnknownOptions)
  },
  { paramKey: "status", type: "array" }
] as const satisfies FilterConfig;

/**
 * MAP tracker detail (`/map/tracker/:tracker_id`): `hrs` = the "last hours"
 * history window index, `pos` = the selected trace-position index. Both live on
 * the map-detail component.
 *
 * `pos`/`hrs`, not `torn`: MapComponent hydrates MAP_LIST_FILTER_CONFIG on this
 * same URL, so a `torn` here would decode as the torn filter too and write the
 * position/window index into `localStorage.filterTorn`. `pos`/`hrs` are safe —
 * no other config on this route declares them.
 */
export const MAP_TRACKER_FILTER_CONFIG = [
  { paramKey: "hrs", type: "number" },
  { paramKey: "pos", type: "number" }
] as const satisfies FilterConfig;

/**
 * The two ANALYTICS radio groups: single source of truth for both the options the
 * radios offer (AnalyticsComponent binds these arrays directly) and the values
 * the URL accepts.
 */
export const ANALYTICS_CONTAINER_CATEGORIES = [
  "all",
  "specific",
  "polyvalent"
] as const;
export const ANALYTICS_CONTAINER_CATEGORY_DEFAULT = "all";
export const ANALYTICS_ALERT_TYPES = ["all", "alert", "warning"] as const;
export const ANALYTICS_ALERT_TYPE_DEFAULT = "alert";

/**
 * ANALYTICS: the tab SUPERSET - every filter any chart can display. Analytics is
 * one route per chart, and each chart declares its own filter set in
 * `graph-config.ts` `frontConfig` (header-analytics.component.html renders a
 * control only when the flag is set), so a chart's URL carries only the subset
 * `analyticsConfigFor()` derives from it: a page must not advertise a param it
 * renders no control for.
 *
 * Only packaging types (`pkg`) persist. The rest live on the component: `cat`
 * packaging category, `range` date range in epoch-ms, `alert` alert type, `span`
 * days, plus the four data-derived multiselects `sites`/`dep`/`arr`/`pack`, whose
 * option lists are built from the response - their default selection is a
 * computed top-N, which the page mirrors out once the data has landed.
 *
 * `pack` (packaging NAMES, analytics-only, read out of the response) is NOT
 * `pkg` (container TYPES, shared with kpi/map/list). The two read alike; they are
 * different filters. `sites` is analytics' own per-chart site axis, rebuilt from
 * every response — map/list's site filter is `lastsite`.
 *
 * `cat` and `alert` are radios: they carry their base value (see the note at the
 * top of this file). `span` is genuinely optional, `range` defaults to a computed
 * window (yesterday, -2 months), and an empty multiselect genuinely means "no
 * site/packaging filter" (every consumer in analytics-controller.service.ts
 * guards with `length > 0`) - so none of them declares a default.
 */
export const ANALYTICS_FILTER_CONFIG = [
  { paramKey: "pkg", storageKey: "filterContainers", type: "array" },
  {
    paramKey: "cat",
    type: "string",
    defaultValue: ANALYTICS_CONTAINER_CATEGORY_DEFAULT,
    allowedValues: ANALYTICS_CONTAINER_CATEGORIES
  },
  { paramKey: "range", type: "range" },
  {
    paramKey: "alert",
    type: "string",
    defaultValue: ANALYTICS_ALERT_TYPE_DEFAULT,
    allowedValues: ANALYTICS_ALERT_TYPES
  },
  { paramKey: "span", type: "number" },
  { paramKey: "sites", type: "array" },
  { paramKey: "dep", type: "array" },
  { paramKey: "arr", type: "array" },
  { paramKey: "pack", type: "array" }
] as const satisfies FilterConfig;

/**
 * The analytics filter superset, as a type. What `analyticsConfigFor` reports it
 * returns, and therefore what `hydrateFromUrl` types the analytics values as.
 */
export type AnalyticsFilterConfig = typeof ANALYTICS_FILTER_CONFIG;

/**
 * ANALYTICS param key -> the `frontConfig` flag that renders its control. The
 * header template gates each control on that same flag, so this map is the one
 * place "the URL carries exactly what the chart displays" is written down.
 */
const ANALYTICS_PARAM_FLAGS: { [paramKey: string]: keyof IGraphFrontConfig } = {
  pkg: "containers",
  cat: "containerCategories",
  range: "dateRange",
  alert: "alertTypes",
  span: "days",
  sites: "sites",
  dep: "departureSites",
  arr: "arrivalSites",
  pack: "packaging"
};

/**
 * The params one chart actually displays a control for.
 *
 * Typed as the whole superset although it returns a subset: which entries survive
 * is a runtime decision (the chart's `frontConfig`), so no static type can follow
 * it. That is deliberate and matches how the page reads the result — a param this
 * chart does not declare simply never lands in the hydrated values, and
 * `applyUrlFilters` guards every one on `!== undefined` for exactly that case.
 */
export function analyticsConfigFor(
  frontConfig?: IGraphFrontConfig
): AnalyticsFilterConfig {
  const shown: FilterConfig = frontConfig
    ? ANALYTICS_FILTER_CONFIG.filter(
        (entry) => !!frontConfig[ANALYTICS_PARAM_FLAGS[entry.paramKey]]
      )
    : [];
  return shown as AnalyticsFilterConfig;
}

/**
 * The superset params that chart does NOT display: the keys a chart switch has to
 * drop from the URL (pass them as `mirrorToUrl`'s `clearKeys`).
 */
export function analyticsUnusedParams(
  frontConfig?: IGraphFrontConfig
): string[] {
  const used = new Set(
    analyticsConfigFor(frontConfig).map((entry) => entry.paramKey)
  );
  return ANALYTICS_FILTER_CONFIG.filter(
    (entry) => !used.has(entry.paramKey)
  ).map((entry) => entry.paramKey);
}

/** Route slug (`/analytics/<slug>`) → `IGraph.name` in graph-config.ts. */
export const ANALYTICS_GRAPH_SLUG_TO_NAME: { [slug: string]: string } = {
  "alerts-stellantis": "redOrangePlant",
  "alerts-suppliers": "redOrangeSupplier",
  "alerts-by-stellantis": "redPlant",
  "alerts-by-suppliers": "redSupplier",
  "storage-time": "storage",
  "transite-time": "transit",
  "stock-level": "stockLevel"
};

/** `IGraph.name` → route slug (inverse of ANALYTICS_GRAPH_SLUG_TO_NAME). */
export const ANALYTICS_GRAPH_NAME_TO_SLUG: { [name: string]: string } =
  Object.keys(ANALYTICS_GRAPH_SLUG_TO_NAME).reduce(
    (acc, slug) => {
      acc[ANALYTICS_GRAPH_SLUG_TO_NAME[slug]] = slug;
      return acc;
    },
    {} as { [name: string]: string }
  );

/**
 * INCIDENT: the filter is a complex `{or/and}` query object and is NOT
 * URL-synced. Only the `/incident/:incident_id` deep-link is supported (handled
 * by the route param, not this config). Present for completeness / symmetry.
 */
export const INCIDENT_FILTER_CONFIG = [] as const satisfies FilterConfig;

/**
 * Which tab loads which of the configs above is not written here: a segment's
 * `filterConfig` is one field of its TAB_SEGMENTS entry (src/app/tabs.ts),
 * alongside the role it needs and the label it shows.
 */
